import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button } from '@mui/material';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import IconMi from 'components/common/icon/IconMi';
import { TestRunFailure, TestRunOffer } from 'api/editor/ai-insight';
import { LlmInitialDataResponse } from 'components/report-content/src/model/cmp/llm';
import useInsightPreviewRun from 'app/extreport/concierge/useInsightPreviewRun';
import InsightPreviewResult from 'app/extreport/concierge/InsightPreviewResult';
import StaticInfo from 'components/common/StaticInfo';
import { Stack } from '@mui/system';

// "Update prompt" writes the offer's prompt into the Prompt field only — nothing persists until
// Apply/Save. On the dataset surface the panel hydrates from the component's last stored failed
// generation (block_component_id prop) so the canvas error line's "see edit panel for details"
// promise holds without a manual Preview run.

interface InsightPreviewInlineProps extends FormControlProps {
    nameField?: string;
}

const DATASET_URL_PREFIX = '/data/editor/report';

export default function InsightPreviewInline({
    controlProps,
    elementProps,
}: FormElementControlPropsType<InsightPreviewInlineProps>) {
    const { t } = useBundleTranslation(elementProps?.translationNS ?? 'app/editor/element/extreport');

    const elementId = elementProps?.componentProps?.element_id as number;
    const aiInsightId = elementProps?.componentProps?.ai_insight_id as number;
    const urlPrefix = elementProps?.componentProps?.urlPrefix as string | undefined;
    const blockComponentId = elementProps?.componentProps?.block_component_id as number | undefined;
    // Dataset Report only: the canvas anchors the saved block to the report instance's measurement_time and
    // its filters; the preview posts the same so both answer the same question.
    const measurementTime = (elementProps?.componentProps?.measurement_time as string | undefined) || '';
    const filters = (elementProps?.componentProps?.filters as string | null | undefined) ?? null;

    const {
        loading,
        previewingSuggested,
        result,
        recovered,
        adopted,
        run,
        runSuggestedPrompt,
        adoptSuggestedPrompt,
        seedFailure,
    } = useInsightPreviewRun(elementId, urlPrefix);

    // Subscribes to the cache entry the canvas component owns (enabled: false — never fetches)
    // to hydrate the failure block from the last stored generation.
    const { data: storedComponentData } = useQuery<LlmInitialDataResponse>({
        queryKey: [`report_block_initial_${blockComponentId}`],
        queryFn: () => Promise.reject(new Error('cache-subscription only')),
        enabled: false,
    });
    useEffect(() => {
        const ai = storedComponentData?.ai;
        if (blockComponentId && ai && ai.status === 'failure') {
            seedFailure({
                status: 'failure',
                failure_message: ai.failure_message ?? '',
                offer: ai.offer ?? undefined,
            } as TestRunFailure);
        }
    }, [blockComponentId, storedComponentData]);

    // Everything a run depends on besides the prompt; a later change to any of it marks the shown result stale.
    const lastRunSnapshot = useRef<{
        prompt: string;
        resources: string;
        primaryResource: string;
        measurementTime: string;
        filters: string | null;
    } | null>(null);

    const isConditional = controlProps.form.hookFormWatch('only_when_significant_ind') === 'Y';
    const fallbackMessage = controlProps.form.hookFormWatch('fallback_message') as string;
    const currentName = controlProps?.nameField
        ? ((controlProps.form.hookFormWatch(controlProps.nameField) as string) ?? '')
        : '';
    const currentPrompt = (controlProps.form.hookFormWatch('prompt') as string) ?? '';
    const currentResources = controlProps.form.hookFormWatch('resources');
    // The picker holds type:id keys; a new External Report form starts with null. Always sent, so the
    // preview runs against the unsaved picker state rather than the stored rows ([] = no additional).
    const currentResourceKeys: string[] = Array.isArray(currentResources)
        ? currentResources.map(String)
        : currentResources
          ? [String(currentResources)]
          : [];
    const currentResourcesKey = JSON.stringify(currentResourceKeys);
    const primaryResource = (controlProps.form.hookFormWatch('primary_resource') as string) ?? '';
    const previewScope = {
        reportAiInsightId: aiInsightId,
        primaryResource,
        resources: currentResourceKeys,
        ...(measurementTime ? { properties: { measurement_time: measurementTime } } : {}),
        filters,
    };
    const snapshotOf = (prompt: string) => ({
        prompt,
        resources: currentResourcesKey,
        primaryResource,
        measurementTime,
        filters,
    });

    const busy = loading || previewingSuggested;

    const isStale =
        !!result &&
        !busy &&
        !!lastRunSnapshot.current &&
        (currentPrompt !== lastRunSnapshot.current.prompt ||
            currentResourcesKey !== lastRunSnapshot.current.resources ||
            primaryResource !== lastRunSnapshot.current.primaryResource ||
            measurementTime !== lastRunSnapshot.current.measurementTime ||
            filters !== lastRunSnapshot.current.filters);

    // The scroll must run after the adopted re-render commits: the recovered box collapses to
    // the small preview box in that commit, which would invalidate a pre-render measurement.
    useEffect(() => {
        if (!adopted) {
            return;
        }
        const textarea = document.querySelector('textarea[name="prompt"]') as HTMLElement | null;
        textarea?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        const glowTimer = setTimeout(() => {
            const inputRoot = textarea?.closest('.MuiInputBase-root') as HTMLElement | null;
            if (!inputRoot) {
                return;
            }
            inputRoot.classList.add('anim-glow-once');
            inputRoot.addEventListener('animationend', () => inputRoot.classList.remove('anim-glow-once'), {
                once: true,
            });
        }, 500);

        return () => clearTimeout(glowTimer);
    }, [adopted]);

    const handlePreview = async () => {
        lastRunSnapshot.current = snapshotOf(currentPrompt);
        await run(
            currentPrompt,
            controlProps.form.hookFormWatch('only_when_significant_ind') as string,
            fallbackMessage,
            previewScope,
        );
    };

    const handleRunSuggestedPrompt = async (offer: TestRunOffer) => {
        if (!offer.suggested_prompt) {
            return;
        }
        // The suggestion runs against the current scope; the Prompt field itself is unchanged until adopted.
        lastRunSnapshot.current = snapshotOf(currentPrompt);
        await runSuggestedPrompt(
            offer.suggested_prompt,
            (result as TestRunFailure | null)?.failure_message ?? '',
            controlProps.form.hookFormWatch('only_when_significant_ind') as string,
            fallbackMessage,
            previewScope,
        );
    };

    // Writes the successfully previewed suggestion into the Prompt field. The programmatic
    // write must not mark the shown result stale, so the snapshot is updated alongside it.
    const handleAdoptSuggestedPrompt = () => {
        const prompt = adoptSuggestedPrompt();
        if (prompt === null) {
            return;
        }
        controlProps.form.hookFormSetValue('prompt', prompt, { shouldDirty: true });
        lastRunSnapshot.current = snapshotOf(prompt);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                <Box>
                    <Button
                        data-test={controlProps.uid ?? 'insight-preview-run'}
                        onClick={handlePreview}
                        disabled={busy}
                        variant="outlined"
                        startIcon={<IconMi icon="play" />}
                    >
                        {t(controlProps.label as string)}
                    </Button>
                </Box>
                <Box flexGrow={1}>
                    <StaticInfo label={t('concierge_insight_test_run_helper')} />
                </Box>
            </Stack>

            {isStale && (
                <Box mt={1}>
                    <StaticInfo type={'warning'} label={t('concierge_insight_test_stale')} />
                </Box>
            )}

            {(busy || result) && (
                <Box mt={2}>
                    <InsightPreviewResult
                        t={t}
                        loading={loading}
                        previewingSuggested={previewingSuggested}
                        result={result}
                        recovered={recovered}
                        adopted={adopted}
                        adoptedCaption={t(
                            urlPrefix === DATASET_URL_PREFIX
                                ? 'concierge_insight_prompt_updated_dataset'
                                : 'concierge_insight_prompt_updated',
                        )}
                        isConditional={isConditional}
                        fallbackMessage={fallbackMessage}
                        idPrefix="insight-preview"
                        onRunSuggestedPrompt={handleRunSuggestedPrompt}
                        onAdoptSuggestedPrompt={handleAdoptSuggestedPrompt}
                        currentName={currentName}
                    />
                </Box>
            )}
        </Box>
    );
}

export class InsightPreviewInlineBuilder extends FormComponentBuilder {
    prepareProps(): InsightPreviewInlineProps {
        return {
            ...this.controlProps,
            nameField: this.elementProps.componentProps?.nameField,
        };
    }
}
